import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Users, FileText, Calendar, TrendingUp, ArrowRight, Brain, Activity } from 'lucide-react';
import { api, formatDate, getScoreLevel, BELIEF_ARCHITECTURE_PATTERNS } from '../../lib/api';

export default function CounselorDashboard() {
  const [clients, setClients] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, followUpsRes] = await Promise.all([
          api.get('/counselor/clients'),
          api.get('/counselor/follow-ups')
        ]);
        setClients(clientsRes.data.clients || []);
        setFollowUps(followUpsRes.data.follow_ups || []);
      } catch (error) {
        console.error('Error fetching counselor data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const activeClients = clients.filter(c => c.latest_assessment);
  const needsAttention = clients.filter(c => {
    const assessment = c.latest_assessment;
    return assessment && assessment.mind_index < 50;
  });

  return (
    <div className="space-y-6" data-testid="counselor-dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total Clients</p>
                  <p className="text-4xl font-bold mt-2 font-mono text-primary">{clients.length}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Active Clients</p>
                  <p className="text-4xl font-bold mt-2 font-mono text-emerald-500">{activeClients.length}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Needs Attention</p>
                  <p className="text-4xl font-bold mt-2 font-mono text-amber-500">{needsAttention.length}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Upcoming Follow-ups</p>
                  <p className="text-4xl font-bold mt-2 font-mono">{followUps.length}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Clients */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Recent Clients</CardTitle>
                <Link to="/counselor/clients">
                  <Button variant="ghost" size="sm">
                    View All
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-14 w-14 mx-auto mb-4 opacity-30" strokeWidth={1} />
                  <p className="font-medium text-foreground/80">No clients yet</p>
                  <p className="text-sm mt-2 max-w-xs mx-auto leading-relaxed">
                    Once your clients complete their diagnostic assessments, they'll appear here for you to review.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clients.slice(0, 5).map((client) => {
                    const assessment = client.latest_assessment;
                    const mindIndex = assessment?.mind_index || 0;
                    const level = getScoreLevel(100 - mindIndex);
                    
                    return (
                      <Link 
                        key={client.user_id}
                        to={`/counselor/client/${client.user_id}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{client.name}</p>
                            <p className="text-xs text-muted-foreground">{client.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold" style={{ color: level.color }}>
                            {mindIndex.toFixed(0)}
                          </p>
                          <p className="text-xs text-muted-foreground">Mind Index</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Upcoming Follow-ups */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Upcoming Follow-ups</CardTitle>
                <Link to="/counselor/follow-ups">
                  <Button variant="ghost" size="sm">
                    View All
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {followUps.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-14 w-14 mx-auto mb-4 opacity-30" strokeWidth={1} />
                  <p className="font-medium text-foreground/80">No upcoming follow-ups</p>
                  <p className="text-sm mt-2 max-w-xs mx-auto leading-relaxed">
                    Schedule follow-up sessions with your clients from their individual profile pages.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {followUps.slice(0, 5).map((followUp) => (
                    <div 
                      key={followUp.note_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                    >
                      <div>
                        <p className="font-medium text-sm">{followUp.client?.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {followUp.notes}
                        </p>
                      </div>
                      <Badge variant="outline">
                        {formatDate(followUp.follow_up_date)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Clients Needing Attention */}
      {needsAttention.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-card border-amber-500/30">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                Clients Needing Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {needsAttention.map((client) => {
                  const assessment = client.latest_assessment;
                  const pattern = assessment?.belief_architecture_pattern || assessment?.cluster_profile;
                  const cluster = BELIEF_ARCHITECTURE_PATTERNS[pattern?.primary_cluster];
                  
                  return (
                    <Link 
                      key={client.user_id}
                      to={`/counselor/client/${client.user_id}`}
                      className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/15 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{client.name}</p>
                        <span className="text-xl font-mono font-bold text-amber-500">
                          {assessment?.mind_index?.toFixed(0)}
                        </span>
                      </div>
                      {cluster && (
                        <Badge variant="outline" className="text-xs">
                          {cluster.name}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
